import Authenticated from '@/components/Authenticated';
import ErrorBoundary from '@/components/ErrorPage/ErrorBoundary';
import Intercom from '@/components/Intercom';
import Layout from '@/components/Layout';
import NotFound from '@/components/NotFound';
import ZendeskMessenger from '@/components/Zendesk/ZendeskMessenger';
import { PENDING_EXPORT_REDIRECT_KEY } from '@/pages/login/Login.util';
import { loggedInRoutes, notLoggedInRoutes } from '@/routes';
import './sass/app.scss';
import store from '@/store';
import { AccountChecker } from '@/support/AccountChecker';
import { ApiIdMapper } from '@/support/ApiId';
import { BrowserHistoryEventBridge } from '@/support/BrowserHistoryEventBridge.tsx';
import { ExpiredJwtDetector } from '@/support/ExpiredJwtDetector.tsx';
import { NetworkStatusDetector } from '@/support/NetworkStatusDetector.tsx';
import { TeamEventProvider } from '@/support/teamEvents/TeamEventProvider.tsx';
import ExportGeneratedToastListener from '@/components/Studies/StudyExports/ExportReadyToast/ExportGeneratedToastListener.tsx';
import { UpdateChecker } from '@/support/UpdateChecker';
import { AlertContainer } from '@/utils/alerts/AlertContainer';
import { DevicesProvider } from '@/utils/devices/DevicesProvider';
import { EntitySchemaProvider } from '@/utils/entitySchema/EntitySchemaProvider';
import { UserTableViewsProvider } from '@/utils/userTableSettings/UserTableViewsProvider';
import { FollowFocusConsumer } from '@/utils/devices/FollowFocusConsumer';
import { type ComponentType, type FC, type ReactNode, useEffect, useRef, useMemo } from 'react';
import { Provider as ReduxProvider, useDispatch } from 'react-redux';
import {
  createBrowserRouter,
  matchPath,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
  useNavigate,
  type NavigateFunction,
} from 'react-router-dom';
import { Bounce, ToastContainer } from 'react-toastify';
import { getJwt } from '@/littledash';

/**
 * Closes any open Redux modal when the route pathname changes.
 *
 * In React Router v5, `<Switch>` fully remounted route components on navigation,
 * which naturally tore down modals. With v7's `createBrowserRouter`, the component
 * tree is more stable, so Redux-managed modals persist across route transitions.
 * This bridge restores the v5 behaviour by dispatching CLOSE_MODAL on every
 * pathname change (including browser back/forward).
 */
const RouteChangeModalCloser: FC = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const previousPathname = useRef(location.pathname);

  useEffect(() => {
    if (previousPathname.current !== location.pathname) {
      dispatch({ type: 'CLOSE_MODAL' });
      previousPathname.current = location.pathname;
    }
  }, [location.pathname, dispatch]);

  return null;
};

type RouteConfig = (typeof loggedInRoutes)[number];
type PublicRouteConfig = (typeof notLoggedInRoutes)[number];

/// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyComponent = ComponentType<any>;
interface RouteComponentProps {
  component: AnyComponent;
  page?: string;
  history?: NavigateFunction;
}
/** Type-safe component renderer for dynamic route components */
const RouteComponent: FC<RouteComponentProps> = ({ component: Component, ...props }) => <Component {...props} />;

/**
 * Root layout component that wraps all routes with necessary providers.
 * This is required for createBrowserRouter to work with context providers.
 */
const RootLayout: FC = () => {
  return (
    <ReduxProvider store={store}>
      <BrowserHistoryEventBridge />
      <RouteChangeModalCloser />
      <ErrorBoundary>
        <TeamEventProvider>
          <DevicesProvider>
            <EntitySchemaProvider>
              <UserTableViewsProvider>
                <FollowFocusConsumer />
                <Outlet />
              </UserTableViewsProvider>
            </EntitySchemaProvider>
          </DevicesProvider>
        </TeamEventProvider>
        <ExpiredJwtDetector />
      </ErrorBoundary>
    </ReduxProvider>
  );
};

/**
 * Creates the browser router with all application routes.
 * Using createBrowserRouter enables data router features like useBlocker.
 */
const createAppRouter = () => {
  const publicRoutes = notLoggedInRoutes.map((route: PublicRouteConfig) => ({
    path: route.path,
    element: <route.component />,
  }));

  const privateRoutes = loggedInRoutes.map((route: RouteConfig) => {
    const skipApiIdMapping = 'skipApiIdMapping' in route && route.skipApiIdMapping === true;
    const inner = (
      <AccountChecker currentPath={route.path}>
        <PrivateRender route={route} />
      </AccountChecker>
    );
    return {
      path: route.path,
      element: <PrivateRoute>{skipApiIdMapping ? inner : <ApiIdMapper>{inner}</ApiIdMapper>}</PrivateRoute>,
    };
  });

  return createBrowserRouter([
    {
      element: <RootLayout />,
      children: [...publicRoutes, ...privateRoutes, { path: '*', element: <NotFound /> }],
    },
  ]);
};

const App = () => {
  // Memoize router to prevent recreation on re-renders
  const router = useMemo(() => createAppRouter(), []);

  return (
    <>
      <RouterProvider router={router} />
      <ToastContainer position="bottom-left" theme="dark" transition={Bounce} closeOnClick={true} icon={false} />
      <AlertContainer />
      <UpdateChecker />
      <NetworkStatusDetector />
    </>
  );
};

interface PrivateRouteProps {
  children: ReactNode;
}

// Routes opt into having their deep-link target stashed in sessionStorage
// before the redirect to /login (via preserveDeepLinkOnLogin in routes.ts), so
// the post-SSO short-lived-token redemption can land users back on the page
// they were trying to reach. Stashing every private route would interact with
// the team-picker — multi-team users would carry the deep-link past their team
// selection — so this is opt-in per route rather than blanket.
const matchesDeepLinkRoute = (pathname: string): boolean =>
  loggedInRoutes.some(
    (route) =>
      'preserveDeepLinkOnLogin' in route &&
      route.preserveDeepLinkOnLogin === true &&
      matchPath(route.path, pathname) !== null
  );

const PrivateRoute: FC<PrivateRouteProps> = ({ children }) => {
  const location = useLocation();
  const storageToken = getJwt();

  if (storageToken === null || storageToken === '') {
    if (matchesDeepLinkRoute(location.pathname)) {
      window.sessionStorage.setItem(PENDING_EXPORT_REDIRECT_KEY, `${location.pathname}${location.search}`);
    }
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

interface PrivateRenderProps {
  route: RouteConfig;
}

const PrivateRender: FC<PrivateRenderProps> = ({ route }) => {
  const navigate = useNavigate();

  if (route.path === '/create-account') {
    return (
      <Authenticated globalData={false}>
        <RouteComponent component={route.component} page={route.name ?? ''} />
      </Authenticated>
    );
  }

  const isDisabled = 'disabled' in route && route.disabled === true;

  return (
    <Authenticated>
      <ExportGeneratedToastListener />
      <Intercom>
        <Layout route={route}>
          <ZendeskMessenger />
          {isDisabled ? (
            <Navigate to="/not-found" replace />
          ) : (
            <RouteComponent component={route.component} page={route.name ?? ''} history={navigate} />
          )}
        </Layout>
      </Intercom>
    </Authenticated>
  );
};

export default App;
